// src/features/sheets/hooks/useTickerValidation.ts
// Hook for validating tickers against the platform's token catalog

import { useMemo } from "react";
import { useTokensCatalog } from "@/hooks/useTokensCatalog";
import { useTokenCategoryMap } from "@/hooks/tokens/compare/useCompareData";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import type { TokenCategoryMap } from "@/utils/tokens";
import type { CategoriesMaps } from "@/hooks/dashboard/useCategoriesTree";

/** Category info for AI prompts */
export interface CategoryWithTickers {
  id: string;
  name: string;
  tickers: string[];
}

export interface TickerValidation {
  /** Set of all known tickers from the token catalog */
  knownTickers: Set<string>;
  /** Check if a ticker exists in the catalog */
  isValidTicker: (ticker: string) => boolean;
  /** Map of ticker -> category IDs */
  tokenCategoryMap: TokenCategoryMap | undefined;
  /** Category maps for looking up category codes/names */
  categoryMaps: CategoriesMaps;
  /** Get category codes for a ticker */
  getTickerCategories: (ticker: string) => string[];
  /** Check if a ticker is in a specific category */
  isTickerInCategory: (ticker: string, categoryCode: string) => boolean;
  /** Get all categories with their tickers (for AI prompts) */
  categoriesWithTickers: CategoryWithTickers[];
  /** Loading state */
  isLoading: boolean;
}

/**
 * Hook that provides ticker validation using the platform's token catalog
 * Returns a set of known tickers and helper functions for validation
 */
export function useTickerValidation(): TickerValidation {
  const { data: tokens, isLoading: tokensLoading } = useTokensCatalog();
  const { data: tokenCategoryMap, isLoading: categoryMapLoading } = useTokenCategoryMap();
  const { maps: categoryMaps, isLoading: categoriesLoading } = useCategoriesTree();

  // Build set of known tickers from token catalog
  const knownTickers = useMemo<Set<string>>(() => {
    if (!tokens) return new Set();
    return new Set(tokens.map((t) => t.ticker.toUpperCase()));
  }, [tokens]);

  // Check if a ticker exists in the catalog
  const isValidTicker = useMemo(() => {
    return (ticker: string): boolean => {
      const upper = ticker.trim().toUpperCase();
      return knownTickers.has(upper);
    };
  }, [knownTickers]);

  // Get category codes for a ticker
  const getTickerCategories = useMemo(() => {
    return (ticker: string): string[] => {
      if (!tokenCategoryMap) return [];
      const upper = ticker.trim().toUpperCase();
      const categoryIds = tokenCategoryMap[upper] || tokenCategoryMap[ticker] || [];

      // Convert category IDs to codes
      const idToCode: Record<number, string> = {};
      for (const [code, id] of Object.entries(categoryMaps.codeToId)) {
        idToCode[id] = code;
      }

      return categoryIds.map((id) => idToCode[id]).filter(Boolean) as string[];
    };
  }, [tokenCategoryMap, categoryMaps.codeToId]);

  // Check if a ticker is in a specific category
  const isTickerInCategory = useMemo(() => {
    return (ticker: string, categoryCode: string): boolean => {
      if (!tokenCategoryMap) return false;
      const upper = ticker.trim().toUpperCase();
      const categoryIds = tokenCategoryMap[upper] || tokenCategoryMap[ticker] || [];
      const targetCategoryId = categoryMaps.codeToId[categoryCode];
      if (targetCategoryId === undefined) return false;
      return categoryIds.includes(targetCategoryId);
    };
  }, [tokenCategoryMap, categoryMaps.codeToId]);

  // Build categories with their tickers for AI prompts
  const categoriesWithTickers = useMemo<CategoryWithTickers[]>(() => {
    if (!tokenCategoryMap || !categoryMaps.codeToId) return [];

    // Build reverse map: category ID -> tickers
    const categoryIdToTickers: Record<number, string[]> = {};
    for (const [ticker, categoryIds] of Object.entries(tokenCategoryMap)) {
      for (const categoryId of categoryIds) {
        if (!categoryIdToTickers[categoryId]) {
          categoryIdToTickers[categoryId] = [];
        }
        categoryIdToTickers[categoryId].push(ticker);
      }
    }

    // Convert to array of CategoryWithTickers
    const result: CategoryWithTickers[] = [];
    for (const [code, id] of Object.entries(categoryMaps.codeToId)) {
      const tickers = categoryIdToTickers[id] || [];
      if (tickers.length > 0) {
        // Get category name from codeToName
        const name = categoryMaps.codeToName[code] || code;
        result.push({
          id: code,
          name,
          tickers: tickers.sort(),
        });
      }
    }

    // Sort by number of tickers (most popular first)
    return result.sort((a, b) => b.tickers.length - a.tickers.length);
  }, [tokenCategoryMap, categoryMaps.codeToId, categoryMaps.codeToName]);

  return {
    knownTickers,
    isValidTicker,
    tokenCategoryMap,
    categoryMaps,
    getTickerCategories,
    isTickerInCategory,
    categoriesWithTickers,
    isLoading: tokensLoading || categoryMapLoading || categoriesLoading,
  };
}
